/**
 * Camera service for capturing photos on mobile devices
 * Uses HTML5 file input with capture attribute for cross-platform compatibility
 */

import { logger } from '../utils/logger';

export interface CameraOptions {
  maxWidth?: number; // Default: 1920
  maxHeight?: number; // Default: 1920
  quality?: number; // Default: 0.8 (JPEG quality 0-1)
}

export class CameraService {
  /**
   * Compress an image file if it exceeds size threshold or dimensions
   * @param file - The image file to potentially compress
   * @param options - Compression options
   * @returns Promise resolving to compressed File or original if compression not needed
   */
  async compressImage(file: File, options: CameraOptions): Promise<File> {
    // Skip compression if file is already small (< 1MB)
    if (file.size <= 1_048_576) {
      logger.debug('[CameraService] Image size OK, skipping compression', {
        sizeKB: (file.size / 1024).toFixed(2)
      });
      return file;
    }

    logger.debug('[CameraService] Compressing image', {
      originalSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      targetQuality: options.quality || 0.8,
    });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Calculate dimensions while maintaining aspect ratio
          const maxWidth = options.maxWidth || 1920;
          const maxHeight = options.maxHeight || 1920;

          let { width, height } = img;

          // Calculate scaling factor
          const scale = Math.min(
            maxWidth / width,
            maxHeight / height,
            1, // Don't upscale
          );

          width = Math.floor(width * scale);
          height = Math.floor(height * scale);

          // Create canvas for compression
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Create new File object with original name
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              logger.debug('[CameraService] Compression complete', {
                originalSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                compressedSize: (compressedFile.size / 1024 / 1024).toFixed(2) + ' MB',
                reduction: ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%',
                dimensions: `${width}x${height}`,
              });

              resolve(compressedFile);
            },
            'image/jpeg',
            options.quality || 0.8,
          );
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for compression'));
        };

        img.src = e.target?.result as string;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Capture a photo using the device camera
   * @param options - Optional camera configuration including compression settings
   * @returns Promise resolving to the captured (and possibly compressed) image File
   * @throws Error if user cancels or no file is selected
   */
  async capture(options: CameraOptions = {}): Promise<File> {
    logger.debug('[CameraService] capture() called with options', { options });

    // Create hidden file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    // Use 'environment' for rear camera (better for troubleshooting photos)
    // Note: Some browsers ignore this, but it's a good hint
    input.capture = 'environment';

    logger.debug('[CameraService] Input element created', {
      type: input.type,
      accept: input.accept,
      capture: input.capture,
    });

    // Hide the input element
    input.style.position = 'fixed';
    input.style.left = '-10000px';
    input.style.top = '-10000px';

    return new Promise((resolve, reject) => {
      // Handle file selection
      input.onchange = (event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        // Clean up: remove input from DOM
        input.remove();

        if (file) {
          // Validate it's an image
          if (!file.type.startsWith('image/')) {
            reject(new Error('Selected file is not an image'));
          return;
        }

          logger.debug('[CameraService] File selected', {
          name: file.name,
          size: (file.size / 1024).toFixed(2) + ' KB',
          type: file.type,
        });

          // Compress the image if needed
        this.compressImage(file, options)
          .then((processedFile) => {
              logger.debug('[CameraService] File ready', {
              name: processedFile.name,
              size: (processedFile.size / 1024).toFixed(2) + ' KB',
              type: processedFile.type,
            });
            resolve(processedFile);
          })
          .catch((error) => {
              logger.error('[CameraService] Compression failed, using original', error as Error);
            // If compression fails, use original file
            resolve(file);
          });
        } else {
          reject(new Error('No image selected'));
        }
      };

      // Handle cancel: Use focus event with timeout
      // When file dialog closes without selection, window regains focus
      const handleCancel = () => {
        setTimeout(() => {
          if (!input.files?.length) {
            input.remove();
            reject(new Error('Camera capture cancelled'));
          }
        }, 300);
      };

      // Add to DOM temporarily (required for some browsers)
      document.body.appendChild(input);
      logger.debug('[CameraService] Input added to DOM');

      // Listen for window focus (indicates dialog closed)
      window.addEventListener('focus', handleCancel, { once: true });

      // Trigger the file picker
      logger.debug('[CameraService] Triggering file picker...');
      input.click();
      logger.debug('[CameraService] input.click() called');
    });
  }

  /**
   * Capture a photo using the device camera with WebRTC (alternative method)
   * @param options - Camera configuration
   * @returns Promise resolving to the captured image File
   */
  async captureWithWebRTC(options: CameraOptions = {}): Promise<File> {
    logger.debug('[CameraService] captureWithWebRTC() called with options', { options });

    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Prefer rear camera
          width: { ideal: options.maxWidth || 1920 },
          height: { ideal: options.maxHeight || 1920 }
        } 
      });
      
      return new Promise((resolve, reject) => {
        // Create video element for camera preview
        const video = document.createElement('video');
        video.srcObject = stream;
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        
        video.addEventListener('loadedmetadata', () => {
          // Create canvas for capture
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          const context = canvas.getContext('2d');
          if (!context) {
            stream.getTracks().forEach(track => track.stop());
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Draw current video frame to canvas
          context.drawImage(video, 0, 0);

          // Convert to blob and create file
          canvas.toBlob((blob) => {
            // Stop camera stream
            stream.getTracks().forEach(track => track.stop());

            if (!blob) {
              reject(new Error('Failed to capture image'));
              return;
            }

            const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
              type: 'image/jpeg'
            });

            // Apply compression if needed
            this.compressImage(file, options)
              .then(resolve)
              .catch(() => resolve(file)); // Fallback to original if compression fails
          }, 'image/jpeg', options.quality || 0.8);
        });
        
        video.addEventListener('error', (error) => {
          stream.getTracks().forEach(track => track.stop());
          reject(new Error(`Camera error: ${error}`));
        });
      });
      
    } catch (error) {
      logger.error('[CameraService] WebRTC camera access error', error as Error);
      throw new Error(`Camera access denied or not available: ${error}`);
    }
  }
}

// Export singleton instance
export const cameraService = new CameraService();